import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { generateStatusId, getSuggestedStatusColor } from '../../FlowchartView/utils/statusUtils';
import './DeleteColumnModal.css';

/**
 * DeleteColumnModal Component
 * 
 * Confirmation modal for deleting a Kanban column (status).
 * Allows user to choose what happens to tasks in that column:
 * - Move to another existing status
 * - Create a new status and move tasks there
 * - Leave tasks statusless (TODO)
 */
const DeleteColumnModal = ({
  isOpen,
  columnName,
  columnId,
  taskCount,
  statuses = [],
  onConfirm,
  onCancel,
}) => {
  const [selectedOption, setSelectedOption] = useState('move'); // 'move' | 'create' | 'statusless'
  const [selectedStatusId, setSelectedStatusId] = useState('');
  const [newStatusName, setNewStatusName] = useState('');
  const newStatusInputRef = useRef(null);
  const confirmBtnRef = useRef(null);

  // Available statuses (exclude the one being deleted)
  const availableStatuses = statuses.filter(s => s.id !== columnId);

  // Auto-select first available status when modal opens
  useEffect(() => {
    if (isOpen) {
      if (availableStatuses.length > 0) {
        setSelectedStatusId(availableStatuses[0].id);
        setSelectedOption('move');
      } else {
        setSelectedOption('create');
      }
      setNewStatusName('');
    }
  }, [isOpen, columnId]);

  // Focus new status input when "create" option is selected
  useEffect(() => {
    if (selectedOption === 'create' && newStatusInputRef.current) {
      newStatusInputRef.current.focus();
    }
  }, [selectedOption]);

  // Focus confirm button when modal opens
  useEffect(() => {
    if (isOpen && confirmBtnRef.current && selectedOption !== 'create') {
      confirmBtnRef.current.focus();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onCancel?.();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onCancel?.();
    }
  };

  const handleConfirm = () => {
    let targetStatusId = null;
    let newStatus = null;

    if (selectedOption === 'move') {
      targetStatusId = selectedStatusId;
    } else if (selectedOption === 'create') {
      if (!newStatusName.trim()) {
        newStatusInputRef.current?.focus();
        return;
      }
      // Create new status object
      newStatus = {
        id: generateStatusId(newStatusName.trim(), statuses),
        name: newStatusName.trim(),
        category: 'in_progress',
        color: getSuggestedStatusColor(statuses),
        order: statuses.length,
      };
      targetStatusId = newStatus.id;
    }
    // If 'statusless', targetStatusId stays null (tasks become TODO)

    onConfirm?.({
      targetStatusId,
      newStatus,
    });
  };

  const modal = (
    <div 
      className="delete-column-modal__backdrop" 
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      <div className="delete-column-modal__container">
        <div className="delete-column-modal__header">
          <div className="delete-column-modal__icon-wrapper">
            <div className="delete-column-modal__orbit" />
            <div className="delete-column-modal__particle" />
            <div className="delete-column-modal__particle delete-column-modal__particle--secondary" />
            <div className="delete-column-modal__icon">
              <span>🗑️</span>
            </div>
          </div>
          <h3 className="delete-column-modal__title">Delete "{columnName}"?</h3>
        </div>
        
        <div className="delete-column-modal__body">
          {taskCount > 0 ? (
            <>
              <p className="delete-column-modal__message">
                This column contains <strong>{taskCount} task{taskCount !== 1 ? 's' : ''}</strong>. 
                Choose what to do with them:
              </p>

              <div className="delete-column-modal__options">
                {/* Option: Move to existing status */}
                {availableStatuses.length > 0 && (
                  <label className={`delete-column-modal__option ${selectedOption === 'move' ? 'delete-column-modal__option--selected' : ''}`}>
                    <input
                      type="radio"
                      name="deleteOption"
                      value="move"
                      checked={selectedOption === 'move'}
                      onChange={() => setSelectedOption('move')}
                    />
                    <div className="delete-column-modal__option-content">
                      <span className="delete-column-modal__option-label">Move to existing status</span>
                      {selectedOption === 'move' && (
                        <select
                          className="delete-column-modal__select"
                          value={selectedStatusId}
                          onChange={(e) => setSelectedStatusId(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {availableStatuses.map(status => (
                            <option key={status.id} value={status.id}>
                              {status.name}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  </label>
                )}

                {/* Option: Create new status */}
                <label className={`delete-column-modal__option ${selectedOption === 'create' ? 'delete-column-modal__option--selected' : ''}`}>
                  <input
                    type="radio"
                    name="deleteOption"
                    value="create"
                    checked={selectedOption === 'create'}
                    onChange={() => setSelectedOption('create')}
                  />
                  <div className="delete-column-modal__option-content">
                    <span className="delete-column-modal__option-label">Create new status</span>
                    {selectedOption === 'create' && (
                      <input
                        ref={newStatusInputRef}
                        type="text"
                        className="delete-column-modal__input"
                        placeholder="New status name..."
                        value={newStatusName}
                        onChange={(e) => setNewStatusName(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleConfirm();
                          }
                        }}
                      />
                    )}
                  </div>
                </label>

                {/* Option: Leave statusless */}
                <label className={`delete-column-modal__option delete-column-modal__option--warning ${selectedOption === 'statusless' ? 'delete-column-modal__option--selected' : ''}`}>
                  <input
                    type="radio"
                    name="deleteOption"
                    value="statusless"
                    checked={selectedOption === 'statusless'}
                    onChange={() => setSelectedOption('statusless')}
                  />
                  <div className="delete-column-modal__option-content">
                    <span className="delete-column-modal__option-label">Leave tasks without status</span>
                    <span className="delete-column-modal__option-hint">Tasks will be set to "TODO" (default)</span>
                  </div>
                </label>
              </div>
            </>
          ) : (
            <p className="delete-column-modal__message">
              This column is empty and can be safely deleted.
            </p>
          )}
        </div>
        
        <div className="delete-column-modal__footer">
          <button 
            className="delete-column-modal__btn delete-column-modal__btn--cancel"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button 
            ref={confirmBtnRef}
            className="delete-column-modal__btn delete-column-modal__btn--danger"
            onClick={handleConfirm}
          >
            Delete Column
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default DeleteColumnModal;

